import { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { LogIn, LayoutGrid, MessageCircle, Calculator } from "lucide-react";
import { PhoneMockup } from "@/components/PhoneMockup";

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  {
    icon: LogIn,
    title: "Connectez-vous partout",
    text: "Webapp sur smartphone, PC, Mac et tablette : votre copropriété vous suit partout, sur tous vos écrans.",
  },
  {
    icon: LayoutGrid,
    title: "Tout est réuni à l'accueil",
    text: "Actualités, calendrier de l'AG, dernier incident et notifications : l'essentiel de votre copropriété en un coup d'œil.",
  },
  {
    icon: MessageCircle,
    title: "Discutez en temps réel",
    text: "La messagerie augmentée réunit occupants, gestionnaires et prestataires dans un seul fil, comme un réseau social privé.",
  },
  {
    icon: Calculator,
    title: "Maîtrisez vos comptes",
    text: "Solde, dépenses et approbations suivies en temps réel : la comptabilité de vos copropriétés, sans tableur.",
  },
];

export const AppJourney = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const i = Math.min(STEPS.length - 1, Math.max(0, Math.floor(p * STEPS.length)));
    setActive((prev) => (prev === i ? prev : i));
  });

  return (
    <section
      ref={ref}
      className="relative hidden bg-mist lg:block"
      style={{ height: `${STEPS.length * 95}vh` }}
      data-testid="app-journey-section"
    >
      <h2 className="sr-only">L'application en action, écran par écran</h2>
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <div className="pointer-events-none absolute -left-24 top-1/4 h-96 w-96 rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] opacity-[0.08] blur-3xl" />
        <div className="pointer-events-none absolute bottom-1/4 right-0 h-96 w-96 rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] opacity-[0.08] blur-3xl" />

        <div className="mx-auto grid w-full max-w-7xl grid-cols-2 items-center gap-10 px-10">
          {/* Étapes synchronisées au scroll */}
          <div className="relative h-72">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={false}
                animate={{ opacity: active === i ? 1 : 0, y: active === i ? 0 : 28 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="absolute inset-0 flex flex-col justify-center"
                data-testid={`journey-step-${i}`}
                aria-hidden={active !== i}
              >
                <span className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-electric">
                  <span className="h-px w-10 bg-electric" />
                  Étape 0{i + 1}
                </span>
                <h3 className="mt-5 text-4xl font-bold tracking-normal leading-[1.15] text-ink xl:text-5xl">
                  {s.title}
                </h3>
                <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60 md:text-lg">
                  {s.text}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Téléphone épinglé, écrans pilotés par le scroll */}
          <div className="flex justify-center">
            <motion.div
              animate={{ scale: [1, 1.015, 1] }}
              transition={{ duration: 0.6, ease: EASE }}
              key={active}
            >
              <PhoneMockup index={active} />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
